import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { addRecentItem } from '../../utils/recentlyOpened';
import { allRoutes } from '../../router/index';
import menuConfig from '../../router/menuConfig';

// 根据路径获取图标
const getIconFromMenuConfig = (pathname) => {
  // 扁平化菜单配置，包括子菜单
  const flattenMenu = (items) => {
    let result = [];
    items.forEach(item => {
      result.push(item);
      if (item.children) {
        result = result.concat(flattenMenu(item.children));
      }
    });
    return result;
  };
  
  const allMenuItems = flattenMenu(menuConfig);
  
  // 优先匹配完整路径
  const exactMatch = allMenuItems.find(item => item.path === pathname);
  if (exactMatch?.icon) {
    return exactMatch.icon;
  }
  
  // 匹配父路径
  const pathSegments = pathname.split('/').filter(Boolean);
  for (let i = pathSegments.length; i > 0; i--) {
    const partialPath = '/' + pathSegments.slice(0, i).join('/');
    const match = allMenuItems.find(item => item.path === partialPath);
    if (match?.icon) {
      return match.icon;
    }
  }
  
  // 默认图标
  return 'FileOutlined';
};

// 匹配路由路径和参数
const matchRoute = (pathname, search) => {
  // 处理带查询参数的路径
  const fullPath = pathname + search;

  // 按优先级排序路由（更具体的路径在前）
  const sortedRoutes = [...allRoutes].sort((a, b) => {
    const aSpecificity = (a.path.match(/:/g) || []).length;
    const bSpecificity = (b.path.match(/:/g) || []).length;
    return aSpecificity - bSpecificity;
  });

  for (const route of sortedRoutes) {
    // 将路由路径转换为正则表达式
    const routePattern = route.path
      .replace(/:[^/]+/g, '[^/]+') // 将 :id 替换为 [^/]+
      .replace(/\//g, '\\/'); // 转义斜杠

    const regex = new RegExp(`^${routePattern}$`);

    if (regex.test(pathname)) {
      return {
        name: route.name,
        path: route.path,
        matchedPath: pathname,
        fullPath: fullPath,
      };
    }
  }

  return null;
};

// 生成页面ID
const generatePageId = (pathname, search) => {
  const searchParams = new URLSearchParams(search);
  const viewId = searchParams.get('view_id');

  if (viewId) {
    return `${pathname.replace(/\//g, '_').substring(1)}_${viewId}`;
  }

  // 对于所有页面，使用路径作为ID
  return pathname.replace(/\//g, '_').substring(1) || 'explore';
};

const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const trackRoute = () => {
      const { pathname, search } = location;

      // 跳过登录页面
      if (pathname === '/login') {
        return;
      }

      // 跳过非正常路由（如 @TaskPage.js 这种文件名）
      if (!pathname.startsWith('/')) {
        return;
      }

      // 匹配路由
      const matchedRoute = matchRoute(pathname, search);

      if (!matchedRoute) {
        console.warn('未找到匹配的路由:', pathname);
        return;
      }

      // 生成页面ID
      const pageId = generatePageId(pathname, search);

      // 获取页面图标 - 从menuConfig中获取图标
      const pageIcon = getIconFromMenuConfig(pathname);

      // 添加到最近打开列表
      addRecentItem({
        id: pageId,
        title: matchedRoute.name,
        path: matchedRoute.fullPath,
        icon: pageIcon,
      });
    };

    trackRoute();
  }, [location]);

  // 这个组件不渲染任何内容
  return null;
};

export default RouteTracker;
